import { z } from "zod";
import { campaignGoalSchema, nextStepPolicySchema } from "./campaign-config.ts";
import { sortJson } from "./contracts.ts";
import { AuthorizedFollowUpPromiseSchema } from "./follow-up-promise.ts";

/**
 * Context value contracts: what a saved context record may hold and how it is
 * shaped. Pure — no database, no filesystem, no clock. `memory.ts` owns the
 * SQLite store built on these and re-exports them.
 *
 * Split out because rule policies need the schemas and must not acquire the
 * store: a bundler pulls in whole modules, not the named exports you asked for.
 */

export const idSchema = z
  .union([z.string().trim().min(1).max(256), z.number().int()])
  .transform(String);
export const instantSchema = z.iso.datetime({ offset: true });
export const timezoneSchema = z
  .string()
  .min(1)
  .refine((value) => {
    if (value !== "UTC" && !value.includes("/")) return false;
    try {
      new Intl.DateTimeFormat("en", { timeZone: value });
      return true;
    } catch {
      return false;
    }
  }, "Explicit valid IANA timezone required");
/** A confirmed event belongs to one campaign; it is never a workspace default. */
export const eventSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(500),
    starts_at: instantSchema,
    timezone: timezoneSchema,
    location: z.discriminatedUnion("kind", [
      z.strictObject({ kind: z.literal("online") }),
      z.strictObject({ kind: z.literal("venue"), venue: z.string().trim().min(1).max(500) }),
    ]),
    registration_link: z.url({ protocol: /^https$/ }),
    capacity: z.number().int().positive().exactOptional(),
    registration_deadline: instantSchema.exactOptional(),
  })
  .refine(
    (event) =>
      event.registration_deadline === undefined ||
      Date.parse(event.registration_deadline) <= Date.parse(event.starts_at),
    { path: ["registration_deadline"], message: "Registration deadline must not follow the event" },
  );
export type CampaignEvent = z.infer<typeof eventSchema>;
export const scopeSchema = z.enum(["client", "campaign", "lead", "person", "rep", "thread"]);
export type Scope = z.infer<typeof scopeSchema>;
/** @internal Non-empty trimmed text, shared by the value schemas and the store. */
export const text = z.string().trim().min(1);
const data = z.union([text, z.array(z.json()), z.record(z.string(), z.json())]);
export const schedulingPolicySchema = z
  .strictObject({
    host_timezone: timezoneSchema,
    working_days: z
      .array(z.number().int().min(0).max(6))
      .min(1)
      .refine((d) => new Set(d).size === d.length)
      .describe(
        "JavaScript weekdays: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6",
      ),
    start_time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    end_time: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    buffer_before_minutes: z.number().int().nonnegative(),
    buffer_after_minutes: z.number().int().nonnegative(),
    min_notice_hours: z.number().nonnegative(),
    max_horizon_days: z.number().int().positive(),
  })
  .refine((p) => p.start_time < p.end_time, {
    path: ["end_time"],
    message: "Must follow start_time; overnight hours unsupported",
  });
export const slotSchema = z
  .strictObject({ start: instantSchema, end: instantSchema })
  .refine((s) => Date.parse(s.start) < Date.parse(s.end), {
    path: ["end"],
    message: "Must follow start",
  });
/**
 * Availability is either verified slots, or the seller's own booking page, or both.
 *
 * A seller who books through a self-serve widget has no slots to offer: the widget resolves them in
 * the lead's browser, in the lead's own timezone. Demanding slots from that seller made every run
 * ask a question the rep could not answer. `booking_url` is the honest value for that flow. It is
 * rep-supplied context like any other, it never proves a booking happened, and a slot list that is
 * present keeps every slot check it had.
 */
export const availabilitySchema = z
  .strictObject({
    slots: z.array(slotSchema),
    booking_url: z.url({ protocol: /^https$/ }).exactOptional(),
    verified_at: instantSchema,
    source: z.enum(["rep", "local_mock"]),
    simulation: z.boolean(),
  })
  .refine((a) => a.source !== "local_mock" || a.simulation, {
    path: ["simulation"],
    message: "Local mock availability requires simulation=true",
  });
/**
 * How this campaign's workspace can take a booking, and the page it books through.
 *
 * The mode is configured, validated and enforced at the edge on every campaign binding, and until
 * now it stopped there: drafting could not tell a workspace that can book from one that never can,
 * so every scheduling reply in a link-only workspace became a question about host hours nobody
 * could answer. `connector` and `manual` keep every scheduling question. `booking_link_only` has no
 * host hours to prove, because the widget resolves them in the lead's browser, so it carries the
 * page instead.
 *
 * Capability informs a choice; it never licenses skipping a gate. Timezone, booking context and
 * client authority are unaffected by this value, and the link-only refinement mirrors the binding's
 * own, so a mode that lost its URL cannot reach the gate claiming to answer it.
 *
 * `manual` is retired for new writes (plan step 9, 2026-10-02) and stays here: recorded histories
 * replay their `loadContext` results through this schema, so dropping it before 2027-01-22T00:00Z,
 * and before a visibility query shows no execution that could have read it, would leave those
 * conversations stuck. `docs/2026-10-02-calendar-connector-seam.md`, "Reading `manual`";
 * `tests/calendar-mode-read-guard.test.ts` and the `e5cd4046/calendar-manual` history guard it.
 */
export const calendarModeSchema = z
  .strictObject({
    mode: z.enum(["booking_link_only", "connector", "manual"]),
    booking_url: z.url({ protocol: /^https$/ }).nullable(),
  })
  .refine((value) => value.mode !== "booking_link_only" || value.booking_url !== null, {
    path: ["booking_url"],
    message: "Booking-link mode needs a URL",
  });
export const bookingContextSchema = z
  .strictObject({
    event_id: text.nullable(),
    calendar_id: text.nullable(),
    status: z
      .enum(["proposed", "confirmed", "tentative", "cancelled", "unknown", "completed", "no_show"])
      .nullable(),
    attendees: z.array(text),
    start: instantSchema.nullish(),
    end: instantSchema.nullish(),
    simulation: z.boolean().optional(),
  })
  .refine((b) => !b.start || !b.end || Date.parse(b.start) < Date.parse(b.end), {
    path: ["end"],
    message: "Must follow start",
  });
/**
 * "Nothing is booked yet", as the booking question itself asks for it: null IDs and status and no
 * attendees. It is the only booking record a rep can give from the app. An existing booking is
 * recorded by Mark booked or a provider receipt, never by a rep typing event and calendar IDs, and
 * whether this absence satisfies the question stays the drafting rule's decision
 * (`context-rules.ts`): it answers a not-yet-booked call and never permits changing one.
 */
export type BookingContextAbsence = {
  event_id: null;
  calendar_id: null;
  status: null;
  attendees: string[];
};
export function bookingContextAbsence(): BookingContextAbsence {
  return { event_id: null, calendar_id: null, status: null, attendees: [] };
}

export function isExplicitBookingAbsence(value: unknown): boolean {
  const parsed = bookingContextSchema.safeParse(value);
  return (
    parsed.success &&
    parsed.data.event_id === null &&
    parsed.data.calendar_id === null &&
    parsed.data.status === null &&
    parsed.data.attendees.length === 0 &&
    parsed.data.simulation !== true
  );
}
/**
 * Links a seller shares alongside a reply, never as its subject: an intro video, a case study.
 *
 * Kept in approved facts, a link read as content to explain, so drafts either dropped it or wrote
 * about it. `label` is what the link is, in the seller's words, and is the only description a reply
 * may give. `share` says when: `first_reply` links it once in the thread's reply that arranges the
 * call, if the seller has not sent it in this thread already; `on_request` only when the lead asks
 * for material.
 */
export const sellerResourceSchema = z.strictObject({
  url: z.url({ protocol: /^https$/ }),
  label: text,
  share: z.enum(["first_reply", "on_request"]),
});
/**
 * Something the reply must respect and never say: "the founder attends the first call".
 *
 * Saved among approved facts, a guard read as sayable authority, and a live draft copied one into the
 * email. A guard is a separate key so the host can keep it from the writer entirely (see
 * `contextAudience`) and so it can never authorize a claim. `avoid` states what the reply must not
 * say or imply; `note` is the operator's reason. Neither is ever customer-facing.
 */
const replyGuardSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]{1,48}$/),
  avoid: text,
  note: text.exactOptional(),
});
/**
 * An answer said only when the lead asks about its topic ("will I be speaking with you?").
 *
 * `answer` is the client's approved content, written as content rather than as a note to the system;
 * a guard's own wording turned sayable is the copy bug this exists to avoid. The writer sees an entry
 * only when the analyst ties it to a question the lead actually asked (see `plannedAnswers`).
 */
export const answerWhenAskedSchema = z.strictObject({
  id: z.string().regex(/^[a-z0-9_]{1,48}$/),
  asked_about: text,
  answer: text,
});
export const contextValueSchemas = {
  campaign_goal: campaignGoalSchema,
  next_step_policy: nextStepPolicySchema,
  recipient_timezone: timezoneSchema,
  rep_timezone: timezoneSchema,
  seller_name: text,
  seller_identity: text,
  seller_offer: z.union([text, z.record(z.string(), z.json())]),
  language: text,
  prospect_name: text,
  rep_name: text,
  meeting_duration_minutes: z.number().int().min(1).max(480),
  event: eventSchema,
  scheduling_policy: schedulingPolicySchema,
  availability: availabilitySchema,
  calendar_mode: calendarModeSchema,
  booking_context: bookingContextSchema,
  approved_facts: data,
  seller_resources: z.array(sellerResourceSchema).min(1),
  reply_guards: z.array(replyGuardSchema).min(1),
  answers_when_asked: z.array(answerWhenAskedSchema).min(1),
  meeting_hours: data,
  tone_preferences: data,
  approved_examples: data,
  feedback: data,
  commitments: data,
  follow_up_promises: z.array(AuthorizedFollowUpPromiseSchema).min(1),
  qualification_context: data,
} as const;
export type ContextKey = keyof typeof contextValueSchemas;
export function isContextKey(value: string): value is ContextKey {
  return Object.hasOwn(contextValueSchemas, value);
}
/** The one question a rep answers by choosing, since its value is a record, not prose. */
export const BOOKING_CONTEXT_KEY = "booking_context" satisfies ContextKey;

/**
 * Context keys whose value is a fact about a particular human rather than about the lead record.
 *
 * `recipient_timezone` is the one with teeth: `AGENTS.md` makes explicit IANA data confirmed for
 * *the recipient* a hard constraint, and two people at one company are routinely in different
 * zones. `prospect_name` is a name, which is a person by definition. Everything else — the offer,
 * the meeting length, availability — describes the engagement, and survives a change of writer.
 *
 * `language` is deliberately absent. It is arguably person-bound, but a colleague usually shares
 * the lead's language, and every key added here becomes a question the rep has to answer.
 */
const IDENTITY_BOUND_CONTEXT_KEYS: ReadonlySet<ContextKey> = new Set<ContextKey>([
  "recipient_timezone",
  "prospect_name",
]);

/**
 * Does a saved context record describe someone other than the person now writing?
 *
 * Withholds only on positive evidence of a difference. A lead-scoped value is a claim about the
 * contact the campaign enrolled, and Email Bison never rebinds that record, so once somebody else
 * replies the value is stale while still carrying a confirmed label. A `thread`-scoped value is
 * already specific — which is also what keeps the resulting question from looping, since the rep's
 * answer is stored at thread scope and this stops firing.
 */
export function writerDiffersFromLead(
  leadEmail: string | null,
  counterparty: string | null,
): boolean {
  const lead = (leadEmail ?? "").trim().toLowerCase();
  const writer = (counterparty ?? "").trim().toLowerCase();
  // Absence is not evidence of a different person, and treating it as such would turn every
  // envelope with no sender address into a context question.
  if (lead.length === 0 || writer.length === 0) return false;
  return lead !== writer;
}

export function savedContextBelongsToAnotherPerson(input: {
  key: ContextKey;
  scope: Scope;
  leadEmail: string | null;
  counterparty: string | null;
}): boolean {
  if (!IDENTITY_BOUND_CONTEXT_KEYS.has(input.key)) return false;
  if (input.scope !== "lead") return false;
  return writerDiffersFromLead(input.leadEmail, input.counterparty);
}
export type ContextValue<K extends ContextKey> = z.infer<(typeof contextValueSchemas)[K]>;
export type JSONValue = z.infer<ReturnType<typeof z.json>>;
export class ContextError extends Error {
  constructor(
    public field: string,
    message: string,
  ) {
    super(`${field}: ${message}`);
    this.name = "ContextError";
  }
}
export const persistedRecordSchema = z.strictObject({
  id: text,
  workspace_id: text,
  scope: scopeSchema,
  scope_id: text,
  key: z.object(contextValueSchemas).keyof(),
  value: z.json(),
  source: z.enum(["rep", "rep_confirmed"]),
  created_at: instantSchema,
  expires_at: instantSchema.nullable(),
  version: z.number().int().positive(),
  revoked_at: instantSchema.nullable(),
  revoke_reason: z.string().nullable(),
});
/** @internal Row shape the store persists. */
export type PersistedRecord = z.infer<typeof persistedRecordSchema>;
/** @internal Shape accepted by the stores write path. */
// Exhaustive field policy: adding a context key requires deciding whether its
// current value can answer the entire question or can contain distinct terms.
// An expandable key accumulates distinct facts, so a rep answer adds to it; any
// other key holds one value, so a rep answer replaces it.
export const contextValuePolicy = {
  campaign_goal: "validated",
  next_step_policy: "validated",
  recipient_timezone: "atomic",
  rep_timezone: "atomic",
  seller_name: "atomic",
  seller_identity: "atomic",
  language: "atomic",
  prospect_name: "atomic",
  rep_name: "atomic",
  meeting_duration_minutes: "atomic",
  event: "validated",
  scheduling_policy: "validated",
  availability: "validated",
  calendar_mode: "validated",
  booking_context: "validated",
  seller_offer: "atomic",
  approved_facts: "expandable",
  seller_resources: "expandable",
  reply_guards: "expandable",
  answers_when_asked: "expandable",
  meeting_hours: "expandable",
  tone_preferences: "expandable",
  approved_examples: "expandable",
  feedback: "expandable",
  commitments: "expandable",
  follow_up_promises: "expandable",
  qualification_context: "expandable",
} as const satisfies Record<ContextKey, "atomic" | "validated" | "expandable">;

/**
 * Scopes a fact may be resolved across, narrowest first.
 *
 * Only these three take part in precedence. `lead`, `person` and `rep` are deliberately absent: the
 * consult of 2026-09-21 fixed precedence as thread > campaign > workspace and said nothing about the
 * others, and inventing an order for them would silently pick a value where the rep is asked today.
 */
export const SCOPE_PRECEDENCE: readonly Scope[] = ["thread", "campaign", "client"];

/**
 * Keys a campaign may override its workspace on, from the enumerated set agreed on 2026-09-21.
 *
 * Every other key keeps today's behaviour: two scopes holding one key is a conflict the rep
 * resolves. The set is deliberately small because a silent cross-scope pick is the failure mode
 * this whole design exists to avoid, so a key earns a place here only by being a fact that two
 * campaigns of one workspace can legitimately disagree on.
 *
 * Two of the six facts the consult named have no FAQ-writable key of their own and so cannot appear
 * here. "Call host" is a field of `scheduling_policy`, which is why that whole policy is
 * overridable rather than a separate host key. "Booking step" is `next_step_policy`, a campaign
 * configuration key that is already campaign-scoped and that `putBatch` refuses from any other
 * writer. `booking_context` is not here either, despite the consult's wording: its schema is a
 * booking record for one thread — event id, attendees, start and end — not a campaign setting.
 *
 * `approved_facts` and `answers_when_asked` joined on 2026-09-24. A campaign launched with no case
 * studies and no prepared terms has to say so for itself, and these are the only keys that can; the
 * rep's campaign-scope Yes/No answer already wrote `approved_facts` at campaign scope, which beside
 * the workspace's own list read as a conflict and held every later reply in that campaign. Both are
 * lists, so a campaign adds or replaces entries and inherits the rest (`resolveScopes`).
 */
export const CAMPAIGN_OVERRIDABLE_CONTEXT_KEYS: ReadonlySet<ContextKey> = new Set<ContextKey>([
  "approved_facts",
  "answers_when_asked",
  "reply_guards",
  "seller_offer",
  "seller_resources",
  "meeting_duration_minutes",
  "event",
  "qualification_context",
  "scheduling_policy",
]);

/**
 * The identity of one entry inside an expandable key's array, which is the unit a narrower scope
 * overrides. Entries that carry their own `id` use it; the rest are identified by their content, so
 * two scopes stating the same fact collapse to one rather than appearing twice.
 */
const expandableEntryIdentitySchema = z.object({
  id: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
});
export function expandableEntryIdentity(entry: JSONValue): string {
  const named = expandableEntryIdentitySchema.safeParse(entry);
  if (named.success) {
    if (named.data.id !== undefined) return `id:${named.data.id}`;
    if (named.data.url !== undefined) return `url:${named.data.url}`;
  }
  return `value:${canonical(entry)}`;
}

/**
 * Which model stages may see each context key: `true`, `false`, or `"planned"` for only the entries the
 * analyst's fact plan ties to what the lead wrote.
 *
 * Every stage used to receive every saved value, so anything saved for the system alone reached the
 * writer as sayable text. This table is exhaustive, so a new key cannot be added without deciding
 * who reads it; `projectPayloadForStage` applies it at the one point where stages are called. Host
 * rules always see everything.
 */
export type ModelStage = "analysis" | "draft" | "grounding" | "prose" | "core";
const everyStage = {
  analysis: true,
  draft: true,
  grounding: true,
  prose: true,
  core: true,
} as const;
/**
 * The core v2 call (`core-v2/contracts.ts`) plans and writes in one, with no analyst to tie an entry
 * to the lead's words, so a `"planned"` column would hide everything. It reads what the analyst
 * reads, except what the writer must never be able to copy: the reply guards, and the follow-up
 * promise records, which carry the authorizer and internal IDs.
 */
export const contextAudience = {
  // Host projection supplies semantics; the raw values contain control-plane routing IDs.
  campaign_goal: { analysis: false, draft: false, grounding: false, prose: false, core: false },
  next_step_policy: { analysis: false, draft: false, grounding: false, prose: false, core: false },
  recipient_timezone: everyStage,
  rep_timezone: everyStage,
  seller_name: everyStage,
  seller_identity: everyStage,
  seller_offer: everyStage,
  language: everyStage,
  prospect_name: everyStage,
  rep_name: everyStage,
  meeting_duration_minutes: everyStage,
  event: everyStage,
  scheduling_policy: everyStage,
  availability: everyStage,
  calendar_mode: everyStage,
  booking_context: everyStage,
  approved_facts: everyStage,
  seller_resources: everyStage,
  reply_guards: { analysis: true, draft: false, grounding: true, prose: false, core: false },
  answers_when_asked: {
    analysis: true,
    draft: "planned",
    grounding: true,
    prose: "planned",
    core: true,
  },
  meeting_hours: everyStage,
  tone_preferences: everyStage,
  approved_examples: everyStage,
  feedback: everyStage,
  commitments: everyStage,
  // The control-plane record includes the authorizer and internal IDs. Stages receive only the open
  // promise for a speech act in the current analysis, projected to a minimal view.
  follow_up_promises: {
    analysis: false,
    draft: "planned",
    grounding: "planned",
    prose: "planned",
    core: false,
  },
  qualification_context: everyStage,
} as const satisfies Record<ContextKey, Record<ModelStage, boolean | "planned">>;

/**
 * A write that states the current value for its exact scope, rather than adding an independent one.
 *
 * The store never picks between two live values, which is right for independent writes and wrong for
 * a rep answering or correcting a question: that left the next run stopped on a conflict the rep had
 * just resolved. A superseding write revokes the live records at its scope in the same transaction,
 * keeping them in history with the reason. With `combine`, the new value is added to the one live
 * value instead, when that value is coherent; a conflict is replaced, never merged.
 */
const supersedeSchema = z.strictObject({ reason: text, combine: z.boolean() });
export const contextWriteSchema = z.strictObject({
  scope: scopeSchema,
  scopeId: idSchema,
  key: persistedRecordSchema.shape.key,
  value: z.json(),
  source: z.enum(["rep", "rep_confirmed"]).default("rep"),
  expiresAt: instantSchema.nullish(),
  supersede: supersedeSchema.exactOptional(),
});
export type ContextWrite = z.input<typeof contextWriteSchema>;

export const ContextRecordSchema = persistedRecordSchema.extend({
  status: z.enum(["active", "expired", "revoked"]),
  expired: z.boolean(),
  authority: z.literal("context_data_only"),
});
const ContextEntrySchema = z.union([
  ContextRecordSchema.extend({
    evidence_records: z.array(text).min(1),
    /**
     * Which scope supplied this value, when more than one held a record for the key.
     *
     * Absent when a single scope answered, which is every entry written before the scope resolver
     * existed, so a caller that does not care about scope reads exactly what it read before.
     */
    resolved_from: z
      .strictObject({ scope: scopeSchema, shadowed: z.array(scopeSchema).min(1) })
      .exactOptional(),
  }),
  z.strictObject({
    key: persistedRecordSchema.shape.key,
    status: z.enum(["conflict", "inactive"]),
    candidates: z.array(ContextRecordSchema).min(1),
    needs_input: z.literal(true),
  }),
  /**
   * A narrower answer is hidden by a broader one that has since been re-answered.
   *
   * The campaign said one thing, the workspace answer it overrode was then changed, and nobody has
   * said whether the campaign still disagrees. Resolving it silently would either ignore the newer
   * workspace answer or discard a deliberate override, so drafting holds for this one fact and the
   * rep confirms with one tap. `shadowing` is the record still in force; `shadowed` is the newer
   * broader record that provoked the doubt.
   */
  z.strictObject({
    key: persistedRecordSchema.shape.key,
    status: z.literal("stale_shadow"),
    shadowing: ContextRecordSchema,
    shadowed: ContextRecordSchema,
    candidates: z.array(ContextRecordSchema).min(1),
    needs_input: z.literal(true),
  }),
]);
export const ContextViewSchema = z.partialRecord(
  persistedRecordSchema.shape.key,
  ContextEntrySchema,
);
export type ContextRecord = z.infer<typeof ContextRecordSchema>;
export type ContextEntry = z.infer<typeof ContextEntrySchema>;
export type ContextView = z.infer<typeof ContextViewSchema>;
export interface ContextSelection {
  campaignId?: string | number;
  leadId?: string | number;
  /** The human writing, resolved by `personScopeId` — alias-aware, and stable across threads. */
  personId?: string | number;
  repId?: string | number;
  clientId?: string | number;
  threadId?: string | number;
}
/** @internal Optimistic-concurrency precondition the store validates. */
export const contextPreconditionSchema = z.strictObject({
  selection: z.strictObject({
    campaignId: idSchema.optional(),
    leadId: idSchema.optional(),
    personId: idSchema.optional(),
    repId: idSchema.optional(),
    clientId: idSchema.optional(),
    threadId: idSchema.optional(),
  }),
  records: ContextViewSchema,
});
export interface ContextPrecondition {
  selection: ContextSelection;
  records: ContextView;
}
/** @internal Stable serialisation used for store equality comparisons. */
export const canonical = (value: JSONValue): string => JSON.stringify(sortJson(value));
